import React, { ReactElement } from 'react'

import { useLocation } from 'react-router-dom'

import styles from './Modal.module.css'
import { ModalFit, modalFit } from './modalFit'

const readFit = (): ModalFit | null =>
  typeof window === 'undefined' ? null : modalFit(window.visualViewport)

export interface ModalProps {
  children: ReactElement
  onRequestClose(e: React.MouseEvent<HTMLElement, MouseEvent> | undefined): void
  open: boolean
}

export default React.memo<ModalProps>(function Modal({
  children,
  onRequestClose,
  open,
}) {
  const location = useLocation()
  React.useEffect(() => {
    const body = document.querySelector('body')

    if (!body) {
      return
    }

    if (open) {
      //window.addEventListener('scroll', disableScroll)
      body.style.overflow = 'hidden'
    } else {
      // window.removeEventListener('scroll', disableScroll)
      body.style.overflow = 'unset'
    }

    // Unmounting (e.g. navigating away from the page) while open === true
    // left the scroll lock in place forever — nothing else in the app ever
    // resets body.style.overflow, so the rest of the site became unscrollable
    // until a full reload. Found by /bug-hunt (2026-09-07).
    return (): void => {
      body.style.overflow = 'unset'
    }
  }, [open, location])

  // Readable size on the zoomed-out phone report (see modalFit.ts).
  const [fit, setFit] = React.useState<ModalFit | null>(readFit)
  React.useEffect(() => {
    if (!open) return undefined
    const update = (): void => setFit(readFit())
    update()
    const vv = window.visualViewport
    vv?.addEventListener('resize', update)
    window.addEventListener('resize', update)
    return (): void => {
      vv?.removeEventListener('resize', update)
      window.removeEventListener('resize', update)
    }
  }, [open])

  const boxStyle: React.CSSProperties | undefined = fit
    ? {
        maxHeight: fit.maxHeight,
        maxWidth: fit.maxWidth,
        transform: fit.scale === 1 ? undefined : `scale(${fit.scale})`,
      }
    : undefined

  // The close button lives INSIDE the white card, top-right (Jaron, Oct 30:
  // the old bare "X" span floated above a transparent box, outside the
  // dialog).
  return (
    <div className={open ? styles['container'] : styles['hidden']}>
      <div
        aria-modal="true"
        className={styles['box']}
        role="dialog"
        style={boxStyle}
      >
        <button
          aria-label="Close"
          className={styles['close']}
          onClick={onRequestClose}
          type="button"
        >
          ×
        </button>
        {children}
      </div>
    </div>
  )
})

// function disableScroll() {
//   var x = window.scrollX
//   var y = window.scrollY
//   window.onscroll = function () {
//     window.scrollTo(x, y)
//   }
// }
